import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { tournamentApi } from '../services/api';

interface Series {
  id: number;
  name: string;
}

interface SeriesAutocompleteProps {
  value: Series | null;
  onChange: (value: Series | null) => void;
  error?: boolean;
  helperText?: string;
}

const SeriesAutocomplete: React.FC<SeriesAutocompleteProps> = ({
  value,
  onChange,
  error,
  helperText,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Series[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Handle input change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch series when search term changes
  useEffect(() => {
    let active = true;

    const fetchSeries = async () => {
      if (searchTerm === '') {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await tournamentApi.getAll({
          search: searchTerm,
          page_size: 10,
        });
        if (active) {
          setOptions(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching series:', error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchSeries();

    return () => {
      active = false;
    };
  }, [searchTerm]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      size='small'
      renderInput={(params) => (
        <TextField
          {...params}
          label="Series"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SeriesAutocomplete;